import classNames from 'classnames';
import { useMemo, useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';
import {
    formatBindingLabel,
    labelWithKey,
    useActionBarKeyLabels,
    useActionBarKeyRebinding,
    useActionBarTrigger,
    useInputBindingPress,
    useInputBindings,
    useSyncActionBarKeys,
} from '@sorrem-client/ui/hooks';
import type { ItemRarity } from '@sorrem/shared';
import { Popover, Separator } from '@sorrem/ui';
import { Callout } from '../Callout';
import { HotkeySlot } from '../HotkeySlot';
import { ACCESSORY_SLOT_KEYS, LOADOUT_SLOT_LABELS, type LoadoutSlotKey } from '@sorrem-client/ui/shared/loadout-slots';
import { ItemSlot } from '../ItemSlot';
import { ResourceBar, type ResourceBarSweep } from '../ResourceBar';
import { LoadoutLabel } from '../LoadoutLabel';
import { LoadoutPanel } from './components/LoadoutPanel';
import styles from './Loadout.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

/** Tint of the player's essence bar; matches the violet the loadout's essence art was drawn in. */
const ESSENCE_BAR_COLOR = '#6b4fd8';

/**
 * Essence as the bar's label shows it: whole, rounded up. A cast needs more
 * essence than it costs, so 24.6 essence shows as 25 and can't pay a cost of
 * 25, just as the host refuses it.
 */
function formatEssence(value: number): number {
    return Math.ceil(value);
}

/** Where an action-frame press came from: its action-bar key, or the pointer on the frame. */
export type LoadoutActionSource = 'key' | 'pointer';

/** An action frame being held down, e.g. to use a crystal. */
export interface LoadoutHeldAction {
    index: number;
    /** `Date.now()` milliseconds when the hold began. */
    startedAtMs: number;
    /** How long it must be held. */
    durationMs: number;
}

/** A first-time callout pointing at one action frame, e.g. at Draw for a new player. */
export interface LoadoutActionCallout {
    index: number;
    label: ReactNode;
    /** Hides it for now. */
    onClose: () => void;
    /** Opts out of it for good. */
    onDismiss: () => void;
}


export interface LoadoutProps {
    className?: string;
    /** Current essence. */
    essence: number;
    /** Size of the essence pool. */
    maxEssence: number;
    /** Shows damage taken on the essence bar, as a draining chip and the amount (in a run, #201). */
    isDamageTakenShown?: boolean;
    /** A highlight sweeping the essence bar (Draw's pulse). */
    essenceSweep?: ResourceBarSweep;
    /**
     * Called with the zero-based action index when a frame is clicked or its
     * action-bar binding is pressed. With `onActionRelease`, it is called when
     * the frame is pressed down rather than clicked.
     */
    onActionActivate?: (index: number, source: LoadoutActionSource) => void;
    /** Called when an action frame or its binding is let go, for actions that must be held. */
    onActionRelease?: (index: number, source: LoadoutActionSource) => void;
    /** The action frame being held, which fills up until the hold completes. */
    heldAction?: LoadoutHeldAction | null;
    /** Action frames to outline as selected. */
    selectedActions?: ReadonlyArray<number>;
    /** Called when an item is dragged onto an equipment or accessory frame, to place it there. */
    onSlotDrop?: (slot: LoadoutSlotKey) => void;
    /** Called when an equipment or accessory frame is clicked, to select what it holds. Frames are inert without it and `onSlotDrop`. */
    onSlotSelect?: (slot: LoadoutSlotKey) => void;
    /** Right-clicking an equipment or accessory frame opens its item's menu beside it (#267). */
    onSlotMenu?: (slot: LoadoutSlotKey, anchor: HTMLElement) => void;
    /** Frames that are valid places for the selected item. */
    highlightedSlots?: ReadonlyArray<LoadoutSlotKey>;
    /** Frames holding the selected item. */
    selectedSlots?: ReadonlyArray<LoadoutSlotKey>;
    /** Lights the nine action frames, e.g. while a carried slot waits to be linked to one. */
    hasHighlightedActions?: boolean;
    /** What each equipment and accessory frame holds, shown in a popover while the pointer rests on it. */
    slotDetails?: Partial<Record<LoadoutSlotKey, ReactNode>>;
    /** The rarity of what each equipment frame holds, shown as the tier's light and badge (#341). */
    slotRarities?: Partial<Record<LoadoutSlotKey, ItemRarity>>;
    /** Content for the armor frame. */
    armor?: ReactNode;
    /** Content for the off-hand frame. */
    offHand?: ReactNode;
    /** Content for the primary frame. */
    primary?: ReactNode;
    /** Content for each of the four accessory frames (row-major, 2x2). */
    accessories?: ReadonlyArray<ReactNode>;
    /** Content for each of the nine action frames. */
    actions?: ReadonlyArray<ReactNode>;
    /** What each action frame does, shown in a popover while the pointer rests on it; none for an empty entry. */
    actionDetails?: ReadonlyArray<ReactNode>;
    /** The action frame whose menu is open, or null. */
    actionMenuIndex?: number | null;
    /**
     * Called with a frame's index when it is right-clicked, and with null when
     * its menu is dismissed (Esc or a click outside). Right-click is inert without it.
     */
    onActionMenuChange?: (index: number | null) => void;
    /** What the open action menu shows, anchored above its frame. */
    actionMenu?: ReactNode;
    /**
     * What the right panel slides out to show past the accessories, e.g. an
     * `EssenceVessel`. With `onVesselOpenChange`, the right end cap toggles it.
     */
    vessel?: ReactNode;
    /** Whether the right panel is slid out to show `vessel`. */
    isVesselOpen?: boolean;
    /** Called when the right end cap is clicked, or the toggle-vessel key pressed, to slide the vessel out or back. */
    onVesselOpenChange?: (open: boolean) => void;
    /** Makes each action frame's key plate a button: click it, then press a key to move the frame onto that key. */
    canRebindKeys?: boolean;
    /** Points a callout at one action frame; none when null. */
    actionCallout?: LoadoutActionCallout | null;
}

export function Loadout({
    className,
    essence,
    maxEssence,
    isDamageTakenShown = false,
    essenceSweep,
    onActionActivate,
    onActionRelease,
    heldAction = null,
    selectedActions = [],
    onSlotDrop,
    onSlotSelect,
    onSlotMenu,
    highlightedSlots = [],
    selectedSlots = [],
    hasHighlightedActions = false,
    slotDetails = {},
    slotRarities = {},
    armor,
    offHand,
    primary,
    accessories = [],
    actions = [],
    actionDetails = [],
    actionMenuIndex = null,
    onActionMenuChange,
    actionMenu,
    vessel,
    isVesselOpen = false,
    onVesselOpenChange,
    canRebindKeys = false,
    actionCallout = null,
}: LoadoutProps): JSX.Element {
    useSyncActionBarKeys();
    const { rebindingIndex, refusal, toggleRebinding } = useActionBarKeyRebinding();

    useActionBarTrigger(
        onActionActivate === undefined ? undefined : (index): void => onActionActivate(index, 'key'),
        onActionRelease === undefined ? undefined : (index): void => onActionRelease(index, 'key'),
    );
    const { bindings } = useInputBindings();
    const toggleVessel = vessel === undefined || onVesselOpenChange === undefined ? undefined : (): void => onVesselOpenChange(!isVesselOpen);
    useInputBindingPress('toggle-vessel', toggleVessel);
    const vesselKey = formatBindingLabel(bindings, 'toggle-vessel');
    const actionLabels = useActionBarKeyLabels();
    const actionFrameRefs = useRef<Array<HTMLDivElement | null>>([]);
    // The popover reads its anchor through a ref; this one follows the open frame.
    const actionMenuAnchorRef = useMemo((): RefObject<HTMLDivElement> => ({
        get current(): HTMLDivElement | null {
            return actionMenuIndex === null ? null : actionFrameRefs.current[actionMenuIndex] ?? null;
        },
    }), [actionMenuIndex]);

    const calloutIndex = actionCallout?.index ?? null;
    const calloutAnchorRef = useMemo((): RefObject<HTMLDivElement> => ({
        get current(): HTMLDivElement | null {
            return calloutIndex === null ? null : actionFrameRefs.current[calloutIndex] ?? null;
        },
    }), [calloutIndex]);

    const slotProps = (slot: LoadoutSlotKey): {
        label: string;
        onPlace: (() => void) | undefined;
        onSelect: (() => void) | undefined;
        onOpenMenu: ((anchor: HTMLElement) => void) | undefined;
        isHighlighted: boolean;
        isSelected: boolean;
        popoverContent: ReactNode;
        rarity: ItemRarity | undefined;
    } => ({
        label: LOADOUT_SLOT_LABELS[slot],
        onPlace: onSlotDrop === undefined ? undefined : (): void => onSlotDrop(slot),
        onSelect: onSlotSelect === undefined ? undefined : (): void => onSlotSelect(slot),
        onOpenMenu: onSlotMenu === undefined ? undefined : (anchor): void => onSlotMenu(slot, anchor),
        isHighlighted: highlightedSlots.includes(slot),
        isSelected: selectedSlots.includes(slot),
        popoverContent: slotDetails[slot],
        rarity: slotRarities[slot],
    });

    return (
        // The data-tour attributes are guided-tour targets (#234), shared by every place the loadout shows.
        <div className={classNames(styles.loadout, isVesselOpen && vessel !== undefined && styles.vesselOpen, className)} {...toTourTargetProps('loadout')}>
            <LoadoutPanel side='left' splitter className={styles.leftPanel} contentClassName={styles.sideGrid}>
                <ItemSlot size='large' {...slotProps('primary')}>{primary}</ItemSlot>
                <ItemSlot size='large' {...slotProps('secondary')}>{offHand}</ItemSlot>
            </LoadoutPanel>

            <LoadoutPanel side='center' className={styles.centerPanel} contentClassName={styles.centerColumn}>
                <div className={styles.essence} {...toTourTargetProps('essence-bar')}>
                    <ResourceBar
                        className={styles.resourceBar}
                        value={essence}
                        maxValue={maxEssence}
                        color={ESSENCE_BAR_COLOR}
                        ariaLabel='Essence'
                        isDamageTakenShown={isDamageTakenShown}
                        sweep={essenceSweep}
                    />
                    {/* The meter is already named Essence, with its values, for assistive tech. */}
                    <span className={styles.essenceLabel} aria-hidden='true'>
                        <LoadoutLabel variant='plate'>
                            Essence <span className={styles.essenceValue}>({formatEssence(essence)} / {formatEssence(maxEssence)})</span>
                        </LoadoutLabel>
                    </span>
                </div>
                {canRebindKeys && rebindingIndex !== null && (
                    <p className={styles.keyPrompt} role='status'>
                        {refusal ?? `Press a key for action ${rebindingIndex + 1}. Esc to cancel.`}
                    </p>
                )}
                <div className={styles.actions} {...toTourTargetProps('action-bar')}>
                    {actionLabels.map((hotkey, index) => (
                        <HotkeySlot
                            key={`action-${index}`}
                            ref={(node): void => { actionFrameRefs.current[index] = node; }}
                            label={rebindingIndex === index ? '...' : hotkey}
                            onLabelClick={canRebindKeys ? (): void => toggleRebinding(index) : undefined}
                            labelAriaLabel={canRebindKeys ? `Change the key for action ${index + 1} (now ${hotkey})` : undefined}
                            isLabelActive={rebindingIndex === index}
                            ariaLabel={`Action ${index + 1}`}
                            ariaKeyShortcuts={hotkey}
                            isHighlighted={hasHighlightedActions}
                            isSelected={selectedActions.includes(index)}
                            popoverContent={actionMenuIndex === index ? undefined : actionDetails[index]}
                            onClick={onActionActivate === undefined || onActionRelease !== undefined
                                ? undefined
                                : (): void => onActionActivate(index, 'pointer')}
                            onPressStart={onActionActivate === undefined || onActionRelease === undefined
                                ? undefined
                                : (): void => onActionActivate(index, 'pointer')}
                            onPressEnd={onActionRelease === undefined ? undefined : (): void => onActionRelease(index, 'pointer')}
                            onSecondaryClick={onActionMenuChange === undefined ? undefined : (): void => onActionMenuChange(index)}
                        >
                            {actions[index]}
                            {heldAction?.index === index && (
                                <span
                                    key={heldAction.startedAtMs}
                                    className={styles.holdProgress}
                                    style={{ animationDuration: `${heldAction.durationMs}ms` } as CSSProperties}
                                    aria-hidden='true'
                                />
                            )}
                        </HotkeySlot>
                    ))}
                </div>
                {actionCallout !== null && (
                    <Callout
                        open={actionMenuIndex === null}
                        anchorRef={calloutAnchorRef}
                        onClose={actionCallout.onClose}
                        onDismiss={actionCallout.onDismiss}
                    >
                        {actionCallout.label}
                    </Callout>
                )}
                {onActionMenuChange !== undefined && (
                    <Popover open={actionMenuIndex !== null} anchorRef={actionMenuAnchorRef} onClose={() => onActionMenuChange(null)}>
                        {actionMenu}
                    </Popover>
                )}
            </LoadoutPanel>

            <LoadoutPanel side='right' splitter className={styles.rightPanel} contentClassName={styles.sideGrid}>
                <ItemSlot size='large' {...slotProps('armor')}>{armor}</ItemSlot>
                <div className={styles.accessories}>
                    {ACCESSORY_SLOT_KEYS.map((slot, index) => (
                        <ItemSlot key={slot} size='small' {...slotProps(slot)}>
                            {accessories[index]}
                        </ItemSlot>
                    ))}
                    <div className={styles.accessoriesLabel}>
                        <LoadoutLabel>Accessories</LoadoutLabel>
                    </div>
                </div>
                {vessel !== undefined && (
                    <>
                        <Separator orientation='vertical' className={styles.vesselSeparator} />
                        {/* A div, not a button: like the HUD panels' side labels, it is a
                            mouse shortcut that never takes focus (focus would keep the
                            game from receiving keys); the toggle-vessel key does the same. */}
                        <div
                            className={styles.vesselLabel}
                            {...toTourTargetProps('vessel')}
                            role='button'
                            aria-label='Vessel'
                            aria-expanded={isVesselOpen}
                            title={labelWithKey('Vessel', vesselKey)}
                            onClick={toggleVessel}
                        >
                            <span aria-hidden='true'>{labelWithKey('Vessel', vesselKey)}</span>
                        </div>
                        <div className={styles.vessel} aria-hidden={!isVesselOpen}>{vessel}</div>
                    </>
                )}
            </LoadoutPanel>
        </div>
    );
}
